import React, { useState, useEffect } from 'react';
import { X, Copy, Layers, Trash2 } from 'lucide-react';
import { roleService } from '../../../services/roleService';

export default function PermissionTemplateModal({ onClose, onApply }) {
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newTemplate, setNewTemplate] = useState({ name: '', description: '' });

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      const data = await roleService?.getPermissionTemplates();
      setTemplates(data);
    } catch (err) {
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  };

  const handleApplyTemplate = async (templateId) => {
    onApply?.(templateId);
    onClose?.();
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!confirm('Are you sure you want to delete this template?')) return;
    
    try {
      await roleService?.deletePermissionTemplate(templateId);
      await loadTemplates();
    } catch (err) {
      setError(err?.message);
    }
  };

  const getAccessLevelColor = (level) => {
    const colors = {
      none: 'bg-gray-100 text-gray-800',
      read: 'bg-blue-100 text-blue-800',
      write: 'bg-green-100 text-green-800',
      delete: 'bg-yellow-100 text-yellow-800',
      admin: 'bg-red-100 text-red-800'
    };
    return colors?.[level] || colors?.none;
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Layers className="w-6 h-6 text-indigo-600" />
            <h2 className="text-xl font-semibold text-gray-900">Permission Templates</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-md transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
              {error}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {templates?.map((template) => (
              <div
                key={template?.id}
                className="border border-gray-200 rounded-lg p-4 hover:border-indigo-300 transition-colors"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-1">
                      <h3 className="text-sm font-semibold text-gray-900">{template?.name}</h3>
                      {template?.isSystemTemplate && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
                          System
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-500">{template?.description}</p>
                  </div>
                  {!template?.isSystemTemplate && (
                    <button
                      onClick={() => handleDeleteTemplate(template?.id)}
                      className="p-1 text-red-600 hover:bg-red-50 rounded-md"
                      title="Delete Template"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>

                {/* Preview permissions */}
                <div className="mb-3">
                  <div className="flex flex-wrap gap-1">
                    {Object.entries(template?.templateData || {})?.slice(0, 8)?.map(([feature, level]) => (
                      <span
                        key={feature}
                        className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getAccessLevelColor(level)}`}
                      >
                        {feature}: {level}
                      </span>
                    ))}
                    {Object.keys(template?.templateData || {})?.length > 8 && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                        +{Object.keys(template?.templateData)?.length - 8} more
                      </span>
                    )}
                  </div>
                </div>

                <button
                  onClick={() => handleApplyTemplate(template?.id)}
                  className="w-full inline-flex items-center justify-center px-3 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700"
                >
                  <Copy className="w-4 h-4 mr-2" />
                  Apply Template
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}